import { supabase } from './supabaseClient';

/**
 * Load restaurants based on user role
 */
export async function loadRestaurants(currentUser) {
  if (!supabase || !currentUser) return [];
  
  try {
    let query = supabase.from('restaurants').select('*').eq('is_archived', false);
    
    // Role-based filtering
    if (currentUser.role === 'marketer') {
      // Marketers see: assigned to them OR unassigned
      query = query.or(`assigned_to.eq.${currentUser.id},assigned_to.is.null`);
    } else if (currentUser.role === 'rep') {
      // Reps see: assigned to them OR ready_for_rep status
      query = query.or(`assigned_rep.eq.${currentUser.id},workflow_status.eq.ready_for_rep`);
    } else if (currentUser.role === 'data_entry') {
      query = query.eq('workflow_status', 'data_review');
    } else if (currentUser.role === 'accountant') {
      query = query.eq('workflow_status', 'invoice_pending');
    }
    
    const { data, error } = await query.order('updated_at', { ascending: false });
    
    if (error) throw error;
    
    // Transform snake_case to camelCase
    return (data || []).map(r => ({
      id: r.id,
      name: r.name,
      ownerName: r.owner_name,
      phone: r.phone,
      area: r.area,
      mapsUrl: r.maps_url,
      notes: r.notes,
      status: r.workflow_status || 'new',
      assignedTo: r.assigned_to,
      assignedRep: r.assigned_rep,
      assignedByRep: r.assigned_by_rep,
      
      // Onboarding fields
      ownerPhone: r.owner_phone,
      whatsappNumber: r.whatsapp_number,
      taxNumber: r.tax_number,
      commissionPercentage: r.commission_percentage,
      signupFee: r.signup_fee,
      deliveryRadiusKm: r.delivery_radius_km,
      defaultDeliveryFee: r.default_delivery_fee,
      minimumOrderAmount: r.minimum_order_amount,
      discountOfferFirst5: r.discount_offer_first_5,
      openTime: r.open_time,
      closeTime: r.close_time,
      accountPassword: r.vendor_password,
      
      // Visit scheduling
      visitDate: r.visit_date,
      visitTime: r.visit_time,
      visitNotes: r.visit_notes,
      visitReportUrl: r.visit_report_url,
      invoiceNotes: r.invoice_notes,
      
      // Files
      files: {
        logoUrl: r.logo_url,
        menu1Url: r.menu_url_1,
        menu2Url: r.menu_url_2,
        invoiceUrl: r.invoice_url,
        tradeLicenseUrl: r.trade_license_url,
        ownerIdUrl: r.owner_id_url,
        paymentReceiptUrl: r.payment_receipt_url,
        restaurantPhotoUrl: r.restaurant_photo_url,
      },
      
      // Status & tracking
      scheduled_call_at: r.scheduled_call_at,
      returnNote: r.return_note,
      returnedBy: r.returned_by,
      returnedAt: r.returned_at,
      paymentMethod: r.payment_method,
      commissionPending: r.commission_pending,
      firstTopupAt: r.first_topup_at,
      createdAt: r.created_at,
      updatedAt: r.updated_at,
      
      // Wrong info fields
      wrongInfoFlag: r.wrong_info_flag || false,
      wrongInfoType: r.wrong_info_type,
      wrongInfoDescription: r.wrong_info_description,
      wrongInfoFlaggedBy: r.wrong_info_flagged_by,
      wrongInfoFlaggedAt: r.wrong_info_flagged_at,
      wrongInfoStatus: r.wrong_info_status || 'pending',
      wrongInfoAdminNote: r.wrong_info_admin_note,
      wrongInfoReviewedBy: r.wrong_info_reviewed_by,
      wrongInfoReviewedAt: r.wrong_info_reviewed_at,
      wrongInfoVerification: r.wrong_info_verification,
      isArchived: r.is_archived || false,
      
      // Claiming system fields
      claimedBy: r.claimed_by,
      claimedAt: r.claimed_at,
      lastInteractionAt: r.last_interaction_at,
    }));
  } catch (err) {
    console.error('❌ Error loading restaurants:', err);
    throw err;
  }
}

/**
 * Create a new restaurant
 */
export async function createRestaurant(restaurant, currentUser) {
  if (!supabase) return null;
  
  try {
    const dbData = {
      name: restaurant.name,
      owner_name: restaurant.ownerName,
      phone: restaurant.phone,
      area: restaurant.area,
      maps_url: restaurant.mapsUrl,
      notes: restaurant.notes,
      workflow_status: restaurant.status || 'new',
      assigned_to: restaurant.assignedTo || null,
      created_by: currentUser.id,
    };
    
    const { data, error } = await supabase
      .from('restaurants')
      .insert([dbData])
      .select()
      .single();
    
    if (error) throw error;
    
    return data;
  } catch (err) {
    console.error('❌ Error creating restaurant:', err);
    throw err;
  }
}

/**
 * Update restaurant with activity logging
 */
export async function updateRestaurant(id, changes, currentUser) {
  if (!supabase) return false;
  
  try {
    // Map camelCase to snake_case
    const dbChanges = {};
    if (changes.name !== undefined) dbChanges.name = changes.name;
    if (changes.ownerName !== undefined) dbChanges.owner_name = changes.ownerName;
    if (changes.phone !== undefined) dbChanges.phone = changes.phone;
    if (changes.area !== undefined) dbChanges.area = changes.area;
    if (changes.mapsUrl !== undefined) dbChanges.maps_url = changes.mapsUrl;
    if (changes.notes !== undefined) dbChanges.notes = changes.notes;
    if (changes.status !== undefined) dbChanges.workflow_status = changes.status;
    if (changes.assignedTo !== undefined) dbChanges.assigned_to = changes.assignedTo;
    if (changes.assignedRep !== undefined) dbChanges.assigned_rep = changes.assignedRep;
    
    // Onboarding fields
    if (changes.ownerPhone !== undefined) dbChanges.owner_phone = changes.ownerPhone;
    if (changes.whatsappNumber !== undefined) dbChanges.whatsapp_number = changes.whatsappNumber;
    if (changes.taxNumber !== undefined) dbChanges.tax_number = changes.taxNumber;
    if (changes.commissionPercentage !== undefined) dbChanges.commission_percentage = changes.commissionPercentage;
    if (changes.signupFee !== undefined) dbChanges.signup_fee = changes.signupFee;
    if (changes.deliveryRadiusKm !== undefined) dbChanges.delivery_radius_km = changes.deliveryRadiusKm;
    if (changes.defaultDeliveryFee !== undefined) dbChanges.default_delivery_fee = changes.defaultDeliveryFee;
    if (changes.minimumOrderAmount !== undefined) dbChanges.minimum_order_amount = changes.minimumOrderAmount;
    if (changes.discountOfferFirst5 !== undefined) dbChanges.discount_offer_first_5 = changes.discountOfferFirst5;
    if (changes.openTime !== undefined) dbChanges.open_time = changes.openTime;
    if (changes.closeTime !== undefined) dbChanges.close_time = changes.closeTime;
    
    // Visit scheduling fields
    if (changes.visitDate !== undefined) dbChanges.visit_date = changes.visitDate || null;
    if (changes.visitTime !== undefined) dbChanges.visit_time = changes.visitTime || null;
    if (changes.visitNotes !== undefined) dbChanges.visit_notes = changes.visitNotes;
    if (changes.invoiceNotes !== undefined) dbChanges.invoice_notes = changes.invoiceNotes;
    if (changes.scheduled_call_at !== undefined) dbChanges.scheduled_call_at = changes.scheduled_call_at;
    if (changes.returnNote !== undefined) dbChanges.return_note = changes.returnNote;
    if (changes.accountPassword !== undefined) dbChanges.vendor_password = changes.accountPassword;
    
    // Payment method field
    if (changes.paymentMethod !== undefined) dbChanges.payment_method = changes.paymentMethod || null;
    if (changes.files) {
      if (changes.files.logoUrl !== undefined) dbChanges.logo_url = changes.files.logoUrl;
      if (changes.files.menu1Url !== undefined) dbChanges.menu_url_1 = changes.files.menu1Url;
      if (changes.files.menu2Url !== undefined) dbChanges.menu_url_2 = changes.files.menu2Url;
      if (changes.files.invoiceUrl !== undefined) dbChanges.invoice_url = changes.files.invoiceUrl;
      if (changes.files.tradeLicenseUrl !== undefined) dbChanges.trade_license_url = changes.files.tradeLicenseUrl;
      if (changes.files.ownerIdUrl !== undefined) dbChanges.owner_id_url = changes.files.ownerIdUrl;
    }
    
    // Wrong info fields
    if (changes.wrongInfoFlag !== undefined) dbChanges.wrong_info_flag = changes.wrongInfoFlag;
    if (changes.wrongInfoType !== undefined) dbChanges.wrong_info_type = changes.wrongInfoType || null;
    if (changes.wrongInfoDescription !== undefined) dbChanges.wrong_info_description = changes.wrongInfoDescription || null;
    if (changes.wrongInfoFlaggedBy !== undefined) dbChanges.wrong_info_flagged_by = changes.wrongInfoFlaggedBy;
    if (changes.wrongInfoFlaggedAt !== undefined) dbChanges.wrong_info_flagged_at = changes.wrongInfoFlaggedAt;
    if (changes.wrongInfoStatus !== undefined) dbChanges.wrong_info_status = changes.wrongInfoStatus;
    if (changes.wrongInfoAdminNote !== undefined) dbChanges.wrong_info_admin_note = changes.wrongInfoAdminNote || null;
    if (changes.wrongInfoReviewedBy !== undefined) dbChanges.wrong_info_reviewed_by = changes.wrongInfoReviewedBy;
    if (changes.wrongInfoReviewedAt !== undefined) dbChanges.wrong_info_reviewed_at = changes.wrongInfoReviewedAt;
    if (changes.wrongInfoVerification !== undefined) dbChanges.wrong_info_verification = changes.wrongInfoVerification;
    if (changes.isArchived !== undefined) dbChanges.is_archived = changes.isArchived;
    
    // Claiming system fields
    if (changes.claimedBy !== undefined) dbChanges.claimed_by = changes.claimedBy;
    if (changes.claimedAt !== undefined) dbChanges.claimed_at = changes.claimedAt;
    if (changes.lastInteractionAt !== undefined) dbChanges.last_interaction_at = changes.lastInteractionAt;
    
    const { error } = await supabase
      .from('restaurants')
      .update(dbChanges)
      .eq('id', id);
    
    if (error) throw error;
    
    // Log activity (non-blocking, silent failure)
    try {
      await logActivity({
        user_id: currentUser.id,
        user_role: currentUser.role,
        action_type: changes.status ? 'status_change' : 'update',
        table_name: 'restaurants',
        record_id: id,
        record_name: changes.name,
        details: { changes: dbChanges },
      });
    } catch (logErr) {
      // Silently ignore logging errors - don't affect main operation
      console.warn('⚠️ Activity logging skipped:', logErr.message);
    }
    
    return true;
  } catch (err) {
    console.error('❌ Error updating restaurant:', err);
    throw err;
  }
}

/**
 * Delete restaurant
 */
export async function deleteRestaurant(id) {
  if (!supabase) return false;
  
  try {
    const { error } = await supabase
      .from('restaurants')
      .delete()
      .eq('id', id);
    
    if (error) throw error;
    return true;
  } catch (err) {
    console.error('❌ Error deleting restaurant:', err);
    throw err;
  }
}

/**
 * Archive a restaurant (soft delete for closed deals)
 */
export async function archiveRestaurant(id, archived = true) {
  if (!supabase) return false;
  
  try {
    const { error } = await supabase
      .from('restaurants')
      .update({ is_archived: archived })
      .eq('id', id);
    
    if (error) throw error;
    return true;
  } catch (err) {
    console.error('❌ Error archiving restaurant:', err);
    throw err;
  }
}

/**
 * Load archived restaurants (admin only)
 */
export async function loadArchivedRestaurants(currentUser) {
  if (!supabase || !currentUser || currentUser.role !== 'admin') return [];
  
  try {
    const { data, error } = await supabase
      .from('restaurants')
      .select('*')
      .eq('is_archived', true)
      .order('updated_at', { ascending: false });
    
    if (error) throw error;
    
    // Transform snake_case to camelCase (same as regular loadRestaurants)
    return (data || []).map(r => ({
      id: r.id,
      name: r.name,
      ownerName: r.owner_name,
      phone: r.phone,
      area: r.area,
      mapsUrl: r.maps_url,
      notes: r.notes,
      status: r.workflow_status || 'new',
      assignedTo: r.assigned_to,
      assignedRep: r.assigned_rep,
      assignedByRep: r.assigned_by_rep,
      claimedBy: r.claimed_by,
      claimedAt: r.claimed_at,
      ownerPhone: r.owner_phone,
      whatsappNumber: r.whatsapp_number,
      taxNumber: r.tax_number,
      files: {
        logoUrl: r.logo_url,
        menu1Url: r.menu_url_1,
        menu2Url: r.menu_url_2,
        invoiceUrl: r.invoice_url,
        tradeLicenseUrl: r.trade_license_url,
        ownerIdUrl: r.owner_id_url,
        paymentReceiptUrl: r.payment_receipt_url,
        restaurantPhotoUrl: r.restaurant_photo_url,
      },
      createdAt: r.created_at,
      updatedAt: r.updated_at,
    }));
  } catch (err) {
    console.error('❌ Error loading archived restaurants:', err);
    return [];
  }
}

/**
 * Bulk archive restaurants
 */
export async function bulkArchiveRestaurants(ids, archived = true) {
  if (!supabase) return { success: 0, failed: 0 };
  
  let success = 0;
  let failed = 0;
  
  for (const id of ids) {
    try {
      const result = await archiveRestaurant(id, archived);
      if (result) success++;
      else failed++;
    } catch (err) {
      console.error(`Failed to archive ${id}:`, err);
      failed++;
    }
  }
  
  return { success, failed };
}

/**
 * Log activity (silent failure - doesn't block main operation)
 */
async function logActivity(activity) {
  try {
    await supabase.from('activity_log').insert([activity]);
  } catch (err) {
    console.error('⚠️ Failed to log activity:', err);
    // Don't throw - logging shouldn't block the main operation
  }
}

// ══════════════════════════════════════════════════════════════
//  CLAIMING SYSTEM
// ══════════════════════════════════════════════════════════════

// Global expiration constant (hours)
export const CLAIM_EXPIRATION_HOURS = 48;

/**
 * Claim a restaurant (lock it to current user)
 * Returns { success: true } or { success: false, reason: string, claimedBy: string }
 */
export async function claimRestaurant(restaurantId, userId) {
  if (!supabase) return { success: false, reason: 'No database connection' };
  
  try {
    // Step 1: Check if still available (atomic check)
    const { data: current, error: fetchError } = await supabase
      .from('restaurants')
      .select('id, claimed_by, claimed_at, name')
      .eq('id', restaurantId)
      .single();
    
    if (fetchError) throw fetchError;
    
    // Step 2: Conflict check
    if (current.claimed_by && current.claimed_by !== userId) {
      // Check if the claim has expired
      const claimedAt = new Date(current.claimed_at);
      const hoursSinceClaim = (Date.now() - claimedAt.getTime()) / (1000 * 60 * 60);
      
      if (hoursSinceClaim < CLAIM_EXPIRATION_HOURS) {
        // Still actively claimed by someone else
        const { data: claimer } = await supabase
          .from('users')
          .select('name')
          .eq('id', current.claimed_by)
          .single();
        
        return { 
          success: false, 
          reason: 'already_claimed',
          claimedByName: claimer?.name || 'مستخدم آخر',
        };
      }
      // Claim expired - we can take it over
    }
    
    // Step 3: Claim it
    const { error: updateError } = await supabase
      .from('restaurants')
      .update({
        claimed_by: userId,
        claimed_at: new Date().toISOString(),
        assigned_to: userId,
      })
      .eq('id', restaurantId);
    
    if (updateError) throw updateError;
    
    return { success: true };
  } catch (err) {
    console.error('❌ Claim error:', err);
    return { success: false, reason: 'error', error: err.message };
  }
}

/**
 * Release a claim (voluntarily or after expiration)
 */
export async function releaseClaim(restaurantId) {
  if (!supabase) return false;
  
  try {
    const { error } = await supabase
      .from('restaurants')
      .update({
        claimed_by: null,
        claimed_at: null,
      })
      .eq('id', restaurantId);
    
    if (error) throw error;
    return true;
  } catch (err) {
    console.error('❌ Release claim error:', err);
    return false;
  }
}

/**
 * Update last interaction timestamp (resets the 48-hour timer)
 */
export async function touchInteraction(restaurantId) {
  if (!supabase) return false;
  
  try {
    const { error } = await supabase
      .from('restaurants')
      .update({ last_interaction_at: new Date().toISOString() })
      .eq('id', restaurantId);
    
    if (error) throw error;
    return true;
  } catch (err) {
    console.error('❌ Touch interaction error:', err);
    return false;
  }
}

/**
 * Check and release expired claims (client-side fallback)
 * Call this on page load or periodically
 */
export async function releaseExpiredClaims() {
  if (!supabase) return 0;
  
  try {
    const expirationTime = new Date(Date.now() - CLAIM_EXPIRATION_HOURS * 60 * 60 * 1000).toISOString();
    
    // Release claims with no interaction after 48 hours
    const { data, error } = await supabase
      .from('restaurants')
      .update({
        claimed_by: null,
        claimed_at: null,
      })
      .lt('claimed_at', expirationTime)
      .is('last_interaction_at', null)
      .neq('workflow_status', 'deal_closed')
      .neq('is_archived', true)
      .select('id');
    
    if (error) throw error;
    
    // Also release where last_interaction is older than 48 hours
    const { data: data2, error: error2 } = await supabase
      .from('restaurants')
      .update({
        claimed_by: null,
        claimed_at: null,
      })
      .lt('last_interaction_at', expirationTime)
      .not('claimed_by', 'is', null)
      .neq('workflow_status', 'deal_closed')
      .neq('is_archived', true)
      .select('id');
    
    if (error2) throw error2;
    
    return (data?.length || 0) + (data2?.length || 0);
  } catch (err) {
    console.error('❌ Release expired claims error:', err);
    return 0;
  }
}

// ══════════════════════════════════════════════════════════════
//  ACCOUNTABILITY & FEEDBACK SYSTEM
// ══════════════════════════════════════════════════════════════

export const FEEDBACK_HOURS_THRESHOLD = 72;

// Reason categories for freeze/delay feedback
export const FEEDBACK_REASONS = [
  { value: 'owner_not_available', label: 'صاحب المطعم غير متاح' },
  { value: 'price_issues', label: 'مشاكل في السعر / التكلفة' },
  { value: 'wrong_location', label: 'موقع خاطئ / بعيد' },
  { value: 'not_joining', label: 'قرر عدم الانضمام' },
  { value: 'busy', label: 'مشغول بعملاء آخرين' },
  { value: 'other', label: 'سبب آخر' },
];

/**
 * Check for pending feedback requirements on page load
 * Returns array of leads that need feedback before they can be released
 */
export async function checkPendingFeedback(userId) {
  if (!supabase) return [];
  
  try {
    const { data, error } = await supabase
      .from('lead_logs')
      .select(`
        id,
        restaurant_id,
        action,
        reason_category,
        reason_text,
        hours_worked,
        created_at,
        restaurants (name, phone, claimed_by)
      `)
      .eq('user_id', userId)
      .eq('action', 'feedback_required')
      .is('reason_text', null)
      .order('created_at', { ascending: false });
    
    if (error) throw error;
    
    return data || [];
  } catch (err) {
    console.error('❌ Check pending feedback error:', err);
    return [];
  }
}

/**
 * Submit feedback for an idle lead (mandatory before release)
 */
export async function submitFeedback(logId, reasonCategory, reasonText) {
  if (!supabase) return { success: false };
  
  try {
    // Step 1: Update the feedback log
    const { error: updateError } = await supabase
      .from('lead_logs')
      .update({
        reason_category: reasonCategory,
        reason_text: reasonText,
      })
      .eq('id', logId)
      .is('reason_text', null);
    
    if (updateError) throw updateError;
    
    // Step 2: Get the restaurant_id from the log
    const { data: log } = await supabase
      .from('lead_logs')
      .select('restaurant_id, user_id, hours_worked')
      .eq('id', logId)
      .single();
    
    if (!log) throw new Error('Log not found');
    
    // Step 3: Release the claim
    const { error: releaseError } = await supabase
      .from('restaurants')
      .update({
        claimed_by: null,
        claimed_at: null,
        workflow_status: 'new',
      })
      .eq('id', log.restaurant_id)
      .eq('claimed_by', log.user_id);
    
    if (releaseError) throw releaseError;
    
    // Step 4: Log the release action
    await supabase.from('lead_logs').insert([{
      restaurant_id: log.restaurant_id,
      user_id: log.user_id,
      action: 'release',
      reason_category: reasonCategory,
      reason_text: reasonText,
      hours_worked: log.hours_worked,
    }]);
    
    return { success: true };
  } catch (err) {
    console.error('❌ Submit feedback error:', err);
    return { success: false, error: err.message };
  }
}

/**
 * Get freeze/release logs for admin view
 */
export async function getFreezeLogs(limit = 50) {
  if (!supabase) return [];
  
  try {
    const { data, error } = await supabase
      .from('lead_logs')
      .select(`
        id,
        restaurant_id,
        user_id,
        action,
        reason_category,
        reason_text,
        hours_worked,
        created_at,
        users (name, role),
        restaurants (name, area, phone)
      `)
      .in('action', ['feedback_required', 'release'])
      .not('reason_text', 'is', null)
      .order('created_at', { ascending: false })
      .limit(limit);
    
    if (error) throw error;
    
    return data || [];
  } catch (err) {
    console.error('❌ Get freeze logs error:', err);
    return [];
  }
}

/**
 * Block user from claiming new leads if they have pending feedback
 */
export async function hasPendingFeedback(userId) {
  if (!supabase) return false;
  
  try {
    const { count, error } = await supabase
      .from('lead_logs')
      .select('*', { count: 'exact', head: true })
      .eq('user_id', userId)
      .eq('action', 'feedback_required')
      .is('reason_text', null)
      .gte('created_at', new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString());
    
    if (error) throw error;
    
    return (count || 0) > 0;
  } catch (err) {
    console.error('❌ Check pending feedback error:', err);
    return false;
  }
}

/**
 * Client-side idle detection: Flag leads that need feedback
 * Alternative to pg_cron for free Supabase plans
 * Call this on page load alongside releaseExpiredClaims
 */
export async function flagIdleLeadsForFeedback() {
  if (!supabase) return 0;
  
  try {
    const thresholdHours = FEEDBACK_HOURS_THRESHOLD;
    const thresholdTime = new Date(Date.now() - thresholdHours * 60 * 60 * 1000).toISOString();
    
    // Find idle claims: claimed > 72h, has phone, not deal_closed
    const { data: idleLeads, error: queryError } = await supabase
      .from('restaurants')
      .select('id, claimed_by, claimed_at, name, phone, workflow_status, status')
      .not('claimed_by', 'is', null)
      .lt('claimed_at', thresholdTime)
      .not('phone', 'is', null)
      .neq('phone', '')
      .neq('workflow_status', 'deal_closed');
    
    if (queryError) throw queryError;
    
    if (!idleLeads || idleLeads.length === 0) return 0;
    
    // Filter for leads that haven't been updated (no registration data)
    const leadsWithoutReceipt = idleLeads.filter(lead => {
      return !lead.ownerName && !lead.ownerPhone && !lead.taxNumber;
    });
    
    // For each idle lead, create a feedback_required log (if not already exists)
    let flagged = 0;
    for (const lead of leadsWithoutReceipt) {
      // Check if already flagged in last 24h
      const { count } = await supabase
        .from('lead_logs')
        .select('*', { count: 'exact', head: true })
        .eq('restaurant_id', lead.id)
        .eq('user_id', lead.claimed_by)
        .eq('action', 'feedback_required')
        .is('reason_text', null)
        .gte('created_at', new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString());
      
      if ((count || 0) === 0) {
        // Not yet flagged - create log
        const hoursWorked = Math.floor((Date.now() - new Date(lead.claimed_at).getTime()) / (1000 * 60 * 60));
        
        const { error: insertError } = await supabase
          .from('lead_logs')
          .insert([{
            restaurant_id: lead.id,
            user_id: lead.claimed_by,
            action: 'feedback_required',
            hours_worked: hoursWorked,
          }]);
        
        if (!insertError) flagged++;
      }
    }
    
    return flagged;
  } catch (err) {
    console.error('❌ Flag idle leads error:', err);
    return 0;
  }
}
